'use client';

import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
	IMobileOperatingSystem,
	getMobileOperatingSystem,
} from '../../utils/getMobileOperatingSystem';
import SmartBannerLogo from '../../assets/airport-logos/smart-banner-logo.jpg';
import { Color } from '../../styles/Color';
import { CloseIcon, Css, UnstyledButton } from '../../ui';
import {
	ANDROID_PACKAGE,
	APPLE_STORE_URL,
	APP_ID_IOS,
	APP_NAME,
	BANNER_KEY,
	PLAY_STORE_URL,
} from './config';
import { isSafari } from '../../utils/isSafari';
import {
	getLocalStorageItem,
	setLocalStorageItem,
} from '../../utils/localStorage';

export function AppBanner() {
	const [isVisible, setIsVisible] = useState(false);
	const [os, setOs] = useState<IMobileOperatingSystem>('unknown');
	const dismissed = getLocalStorageItem(BANNER_KEY);

	useEffect(() => {
		const detectedOs = getMobileOperatingSystem();
		setOs(detectedOs);
	}, []);

	useEffect(() => {
		if (!dismissed && (os === 'iOS' || os === 'Android')) {
			setIsVisible(true);
		}
	}, [os, dismissed]);

	const handleClose = () => {
		setIsVisible(false);
		setLocalStorageItem(BANNER_KEY, 'true');
	};

	const handleOpen = () => {
		const fallbackTimeout = setTimeout(() => {
			if (os === 'iOS') {
				window.location.href = `${APPLE_STORE_URL}/id${APP_ID_IOS}`;
			} else if (os === 'Android') {
				window.location.href = `${PLAY_STORE_URL}?id=${ANDROID_PACKAGE}`;
			}
		}, 1500);

		// Cancel fallback if user leaves the page (app opened)
		window.addEventListener('pagehide', () => clearTimeout(fallbackTimeout));
	};

	/* The smart banner on Safari is already implemented via the metadata */
	if (!isVisible || (isSafari() && os === 'iOS')) return null;

	return (
		<BannerContainer>
			<BannerContent>
				<BannerCloseButton
					type="button"
					onClick={handleClose}
					aria-label="Close banner"
				>
					<CloseIcon aria-hidden />
				</BannerCloseButton>
				<img
					src={SmartBannerLogo}
					alt={`${APP_NAME} icon`}
					width={60}
					height={60}
					className="smart-banner-logo"
				/>
				<div>
					<BannerTextHeader>{APP_NAME}</BannerTextHeader>
					<BannerTextSubtitle>Travel</BannerTextSubtitle>
				</div>
			</BannerContent>

			<OpenButtonContainer>
				<button
					type="button"
					onClick={handleOpen}
					className="banner-open-app-button"
				>
					Get
				</button>
			</OpenButtonContainer>
		</BannerContainer>
	);
}

const BannerContainer = styled.div`
	position: static;
	top: 0;
	width: 100%;
	background-color: ${Color.Neutral1};
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: ${Css.pxToRem(10)} ${Css.pxToRem(12)};
	z-index: 9999;

	.smart-banner-logo {
		border-radius: 14px;
	}
`;

const BannerContent = styled.div`
	display: flex;
	gap: ${Css.pxToRem(10)};
`;

const BannerCloseButton = styled(UnstyledButton)`
	svg {
		height: 16px;
		width: 16px;

		path {
			fill: ${Color.Neutral4};
		}
	}
`;

const BannerTextHeader = styled.div`
	font-weight: 500;
`;

const BannerTextSubtitle = styled.div`
	font-size: ${Css.pxToRem(14)};
	color: ${Color.Neutral4};
`;

const OpenButtonContainer = styled.div`
	display: flex;
	align-items: center;

	.banner-open-app-button {
		background: #0070f3;
		color: ${Color.Neutral1};
		padding: ${Css.pxToRem(6)} ${Css.pxToRem(18)};
		border-radius: 20px;
		border: none;
		font-weight: 600;
		font-size: ${Css.pxToRem(16)};
	}
`;
